// The Studio side of the preview bridge (ARCHITECTURE §9; packages/contracts/schemas/bridge.schema.json;
// the preview side is packages/sdk/src/runtime/bridge.ts). AC-12: the preview lives on its own origin
// without Studio cookies; the Studio accepts a message only from that origin and from that frame,
// checks its shape, and never evaluates anything in it.

export type BridgeMode = 'off' | 'section' | 'element';
export type Rect = { x: number; y: number; width: number; height: number };
/** A section, or an element inside it (D21 C3): `el` is the builder's `data-el` id. */
export type BridgeTarget = { section_id: string; el?: string | null; tag?: string; outer_html_excerpt?: string; rect?: Rect };

export type PreviewMessage =
  | { type: 'ready'; payload: { build_id: string; locale?: string; sections: { id: string; rect?: Rect }[]; height: number } }
  | { type: 'hover'; payload: { target: BridgeTarget | null } }
  | { type: 'select'; payload: { target: BridgeTarget } }
  | { type: 'resize'; payload: { width: number; height: number } }
  | { type: 'error'; payload: { code: string; message?: string } };

const SECTION_ID = /^[a-z0-9][a-z0-9-]*$/;
const EL_ID = /^[a-z0-9-]+(\.[0-9]+)*$/;
const CODE = /^[a-z][a-z0-9_]*$/;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function isRect(v: unknown): v is Rect {
  return isObj(v) && isNum(v.x) && isNum(v.y) && isNum(v.width) && isNum(v.height) && v.width >= 0 && v.height >= 0;
}

function isTarget(v: unknown): v is BridgeTarget {
  if (!isObj(v) || typeof v.section_id !== 'string' || v.section_id.length > 64 || !SECTION_ID.test(v.section_id)) return false;
  if (v.el !== undefined && v.el !== null && (typeof v.el !== 'string' || v.el.length > 200 || !EL_ID.test(v.el))) return false;
  if (v.tag !== undefined && (typeof v.tag !== 'string' || v.tag.length > 32)) return false;
  if (v.outer_html_excerpt !== undefined && (typeof v.outer_html_excerpt !== 'string' || v.outer_html_excerpt.length > 2000)) return false;
  if (v.rect !== undefined && !isRect(v.rect)) return false;
  return true;
}

/** The message if it is a well-formed preview → Studio message (bridge.schema.json), else null. */
export function parsePreviewMessage(data: unknown): PreviewMessage | null {
  if (!isObj(data) || data.source !== 'aiss-preview' || data.v !== 1 || typeof data.type !== 'string' || !isObj(data.payload)) return null;
  const p = data.payload;
  switch (data.type) {
    case 'ready':
      if (typeof p.build_id !== 'string' || !isNum(p.height) || !Array.isArray(p.sections) || p.sections.length > 100) return null;
      if (!p.sections.every((s) => isObj(s) && typeof s.id === 'string' && SECTION_ID.test(s.id) && (s.rect === undefined || isRect(s.rect)))) return null;
      return {
        type: 'ready',
        payload: { build_id: p.build_id, locale: typeof p.locale === 'string' ? p.locale : undefined, sections: p.sections as { id: string; rect?: Rect }[], height: p.height },
      };
    case 'hover':
      return p.target === null || isTarget(p.target) ? { type: 'hover', payload: { target: (p.target as BridgeTarget | null) ?? null } } : null;
    case 'select':
      return isTarget(p.target) ? { type: 'select', payload: { target: p.target } } : null;
    case 'resize':
      return isNum(p.width) && isNum(p.height) ? { type: 'resize', payload: { width: p.width, height: p.height } } : null;
    case 'error':
      return typeof p.code === 'string' && CODE.test(p.code) && p.code.length <= 64 ? { type: 'error', payload: { code: p.code, message: typeof p.message === 'string' ? p.message.slice(0, 500) : undefined } } : null;
    default:
      return null;
  }
}

/** A Studio → preview message. */
export function studioMessage(type: 'init' | 'mode' | 'highlight' | 'scroll_to' | 'device' | 'rebuild', payload: Record<string, unknown>) {
  return { source: 'aiss-studio', v: 1, type, payload };
}

/**
 * The preview's origin, or null when the URL may not be framed: it must be http(s) and **another
 * origin than the Studio's** (AC-12: a same-origin preview could read Studio cookies and storage).
 */
export function previewOrigin(url: string, studioOrigin: string): string | null {
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    return u.origin === studioOrigin ? null : u.origin;
  } catch {
    return null;
  }
}

/** The device frames (C2, PLAN D21: Tablet is 834 px). */
export const DEVICE_WIDTH = { desktop: null, tablet: 834, mobile: 390 } as const;
export type Device = keyof typeof DEVICE_WIDTH;
